import { useCallback, useEffect, useRef, useState } from 'react';
import { adoptAuthenticatedOwnerWorkspace } from '@/lib/owner-bootstrap';
import { finishOwnerAdoptionCleanup } from '@/lib/owner-browser-state';
import {
  CLIENT_UPDATE_REQUIRED_EVENT,
  isClientUpdateRequiredError,
} from '@/lib/db';
import {
  getContentOwnerBinding,
  inspectUnboundContentOwnerProfileDetailed,
} from '@/lib/content-owner';
import {
  decideOwnerGateStatus,
  retainLoadedBinding,
  retainLoadedSession,
  retainUnreadableBinding,
  startOwnerStorageRecheck,
  type LoadedBinding,
  type LoadedSession,
  type OwnerBootstrapResolution,
  type OwnerGateStatus,
} from '@/lib/owner-gate-state';
import { getSupabase } from '@/lib/supabase';
import {
  holdSharedOwnerRuntimeLock,
  isOwnerTransitionPresentationBarrierActive,
  subscribeOwnerTransitionBarrier,
} from '@/lib/owner-transition';

export type { OwnerGateStatus } from '@/lib/owner-gate-state';

/**
 * How one user-requested storage check ended (status `storage-unreadable`):
 * storage is still unreadable (the screen stays and says so); it reads again
 * and the normal gate continues; the account or an owner transition changed
 * meanwhile, so the result was discarded; or a check was already running.
 */
export type OwnerStorageCheckResult =
  | 'still-unreadable'
  | 'continued'
  | 'discarded'
  | 'already-running';

export type OwnerGateResult = {
  status: OwnerGateStatus;
  knownOwnerId: string | null;
  currentOwnerId: string | null;
  retry: () => void;
  /** User-triggered only: never on a timer. One check at a time. */
  checkStorageAgain: () => Promise<OwnerStorageCheckResult>;
};

type AttemptResult = {
  ownerId: string;
  previousOwnerId: string | null;
  attempt: number;
  resolution: OwnerBootstrapResolution;
};

type MatchingOwnerReady = {
  ownerId: string;
  attempt: number;
  state: 'ready' | 'error';
};

type RuntimeLease = {
  ownerId: string;
  attempt: number;
  barrierEpoch: number;
  state: 'ready' | 'error';
};

/**
 * Resolve authentication and local ownership before any owner-bearing route
 * or worker mounts. An unbound profile with protected bytes stops at encrypted
 * export/recovery; only a proven-empty profile may install authenticated server
 * history. A different bound owner follows that path only when no positively
 * identified unsynced work exists.
 */
export function useOwnerGate(): OwnerGateResult {
  const [binding, setBinding] = useState<LoadedBinding | null>(null);
  const [session, setSession] = useState<LoadedSession | null>(() =>
    getSupabase() ? null : { userId: null },
  );
  const [attempt, setAttempt] = useState(0);
  const [attemptResult, setAttemptResult] = useState<AttemptResult | null>(null);
  const [matchingOwnerReady, setMatchingOwnerReady] =
    useState<MatchingOwnerReady | null>(null);
  const [transitionBarrier, setTransitionBarrier] = useState(() => ({
    active: isOwnerTransitionPresentationBarrierActive(),
    epoch: 0,
  }));
  const [runtimeLease, setRuntimeLease] = useState<RuntimeLease | null>(null);
  const [clientUpdateRequired, setClientUpdateRequired] = useState(false);
  const currentSessionOwnerRef = useRef<string | null>(null);
  const storageCheckGuardRef = useRef({ inFlight: false });
  const mountedRef = useRef(true);
  // Counts owner-transition barrier notifications, so a storage check can tell
  // that a transition began or ended while it was reading.
  const barrierNoticesRef = useRef(0);
  // Advances on every change of the session's owner, including A to B to A,
  // like the app-wide auth epoch (keepsake/epochs.ts). That one is wired only
  // when the owner runtime mounts, which never happens behind a closed screen.
  const sessionOwnerChangesRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const onUpdateRequired = () => setClientUpdateRequired(true);
    window.addEventListener(CLIENT_UPDATE_REQUIRED_EVENT, onUpdateRequired);
    return () => window.removeEventListener(CLIENT_UPDATE_REQUIRED_EVENT, onUpdateRequired);
  }, []);

  // A failed binding read is recorded as unreadable, never as unbound: an
  // unbound result would send the profile down the inspection and adoption
  // path, and a profile whose binding cannot be read may belong to another
  // account (rule #87).
  useEffect(() => {
    let cancelled = false;
    getContentOwnerBinding()
      .then((value) => {
        if (!cancelled) setBinding((current) => retainLoadedBinding(current, value));
      })
      .catch((error) => {
        if (isClientUpdateRequiredError(error)) setClientUpdateRequired(true);
        if (!cancelled) setBinding((current) => retainUnreadableBinding(current));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => subscribeOwnerTransitionBarrier((active) => {
    barrierNoticesRef.current += 1;
    setTransitionBarrier((current) => ({
      active,
      epoch: current.epoch + 1,
    }));
  }), []);

  useEffect(() => {
    const client = getSupabase();
    if (!client) return;
    let cancelled = false;
    const noteSessionOwner = (userId: string | null) => {
      if (currentSessionOwnerRef.current !== userId) sessionOwnerChangesRef.current += 1;
      currentSessionOwnerRef.current = userId;
    };
    client.auth
      .getSession()
      .then(({ data }) => {
        if (cancelled) return;
        const userId = data.session?.user.id ?? null;
        noteSessionOwner(userId);
        setSession((current) => retainLoadedSession(current, userId));
      })
      .catch(() => {
        if (cancelled) return;
        noteSessionOwner(null);
        setSession((current) => retainLoadedSession(current, null));
      });
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event, nextSession) => {
      const userId = nextSession?.user.id ?? null;
      noteSessionOwner(userId);
      setSession((current) => retainLoadedSession(current, userId));
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  // Auth may change at the same instant an install transaction commits. A
  // fresh binding read on every observed session closes that boundary and
  // lets the next account continue from the durable owner actually on disk.
  useEffect(() => {
    if (session === null) return;
    let cancelled = false;
    void getContentOwnerBinding()
      .then((value) => {
        if (!cancelled) setBinding((current) => retainLoadedBinding(current, value));
      })
      .catch((error) => {
        if (isClientUpdateRequiredError(error)) setClientUpdateRequired(true);
        if (!cancelled) setBinding((current) => retainUnreadableBinding(current));
      });
    return () => {
      cancelled = true;
    };
  }, [session, transitionBarrier.epoch]);

  // A matching binding may carry the durable post-install cleanup marker from
  // a prior interrupted page. Finish that handshake before returning `ok`.
  useEffect(() => {
    if (binding === null || session === null) return;
    const ownerId = session.userId;
    if (!ownerId || binding.value !== ownerId) return;
    if (
      matchingOwnerReady?.ownerId === ownerId &&
      matchingOwnerReady.attempt === attempt
    ) return;
    let cancelled = false;
    void finishOwnerAdoptionCleanup(ownerId)
      .then(() => {
        if (!cancelled && currentSessionOwnerRef.current === ownerId) {
          setMatchingOwnerReady({ ownerId, attempt, state: 'ready' });
        }
      })
      .catch((error) => {
        if (isClientUpdateRequiredError(error)) setClientUpdateRequired(true);
        if (!cancelled && currentSessionOwnerRef.current === ownerId) {
          setMatchingOwnerReady({ ownerId, attempt, state: 'error' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [attempt, binding, matchingOwnerReady, session]);

  useEffect(() => {
    if (binding === null || session === null) return;
    // Unreadable is not unbound: no inspection, adoption or switch starts.
    if (binding.unreadable === true) return;
    const ownerId = session.userId;
    const previousOwnerId = binding.value;
    if (!ownerId || previousOwnerId === ownerId) return;
    if (
      attemptResult?.ownerId === ownerId &&
      attemptResult.previousOwnerId === previousOwnerId &&
      attemptResult.attempt === attempt
    ) return;

    let cancelled = false;
    const controller = new AbortController();
    const isCurrent = () =>
      !cancelled &&
      !controller.signal.aborted &&
      currentSessionOwnerRef.current === ownerId;
    void (async () => {
      // An unbound profile is not proof of an empty profile. Older releases
      // created valid device-only prayers before owner binding existed. Keep
      // those bytes behind the recovery/export surface instead of replacing
      // them with the authenticated server snapshot.
      // Storage that cannot be inspected is kept apart from content actually
      // found: both stay closed, but they get different, honest screens.
      if (previousOwnerId === null) {
        const profile = await inspectUnboundContentOwnerProfileDetailed();
        if (!isCurrent()) return { status: 'cancelled' as const };
        if (profile === 'unreadable') return { status: 'unreadable' as const };
        if (profile === 'protected') return { status: 'protected' as const };
      }
      const result = await adoptAuthenticatedOwnerWorkspace({
        ownerId,
        previousOwnerId,
        isAuthenticatedOwnerCurrent: isCurrent,
        abortSignal: controller.signal,
      });
      return { status: 'adopted' as const, result };
    })()
      .then(async (result) => {
        if (!isCurrent()) return;
        if (result.status === 'cancelled') return;
        if (result.status === 'unreadable') {
          setAttemptResult({
            ownerId,
            previousOwnerId,
            attempt,
            resolution: 'storage-unreadable',
          });
          return;
        }
        if (result.status === 'protected') {
          setAttemptResult({
            ownerId,
            previousOwnerId,
            attempt,
            resolution: 'unidentified-content',
          });
          return;
        }
        if (result.result.status === 'protected-content') {
          setAttemptResult({
            ownerId,
            previousOwnerId,
            attempt,
            resolution: 'unidentified-content',
          });
          return;
        }
        if (result.result.status === 'pending-work') {
          setAttemptResult({
            ownerId,
            previousOwnerId,
            attempt,
            resolution: 'switch-pending',
          });
          return;
        }
        await finishOwnerAdoptionCleanup(ownerId);
        if (!isCurrent()) return;
        setBinding((current) => retainLoadedBinding(current, ownerId));
        setMatchingOwnerReady({ ownerId, attempt, state: 'ready' });
        setAttemptResult({
          ownerId,
          previousOwnerId,
          attempt,
          resolution: 'installed',
        });
      })
      .catch(async (error) => {
        if (!isCurrent()) return;
        if (isClientUpdateRequiredError(error)) {
          setClientUpdateRequired(true);
          return;
        }
        const currentBinding = await getContentOwnerBinding().catch(() => previousOwnerId);
        if (!isCurrent()) return;
        if (currentBinding === ownerId) {
          setBinding((current) => retainLoadedBinding(current, ownerId));
          setMatchingOwnerReady({ ownerId, attempt, state: 'error' });
          return;
        }
        setAttemptResult({
          ownerId,
          previousOwnerId,
          attempt,
          // Pending local work, or a drain refused because this page (or
          // another) still holds an unsaved recording: the switch waits; it is
          // never reported as a connection problem (plan rev 4.1, 6.4).
          resolution:
            error instanceof Error && (
              error.message === 'owner-switch-pending-work' ||
              error.message === 'owner-transition-drain-failed'
            )
              ? 'switch-pending'
              : 'connection-error',
        });
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [attempt, attemptResult, binding, session]);

  const ownerId = session?.userId ?? null;
  const matchingReady = Boolean(
    ownerId &&
    binding?.value === ownerId &&
    matchingOwnerReady?.ownerId === ownerId &&
    matchingOwnerReady.attempt === attempt &&
    matchingOwnerReady.state === 'ready',
  );
  const matchingFailed = Boolean(
    ownerId &&
    binding?.value === ownerId &&
    matchingOwnerReady?.ownerId === ownerId &&
    matchingOwnerReady.attempt === attempt &&
    matchingOwnerReady.state === 'error',
  );
  const activeAttempt =
    ownerId &&
    attemptResult?.ownerId === ownerId &&
    attemptResult.previousOwnerId === (binding?.value ?? null) &&
    attemptResult.attempt === attempt
      ? attemptResult
      : null;
  const preLeaseStatus = clientUpdateRequired
    ? 'client-update-required'
    : matchingFailed
    ? 'connection-error'
    : decideOwnerGateStatus({
        bindingLoaded: binding !== null,
        bindingUnreadable: binding?.unreadable === true,
        sessionLoaded: session !== null,
        sessionOwnerId: ownerId,
        bindingOwnerId: binding?.value ?? null,
        matchingBindingReady: matchingReady,
        bootstrapResolution: activeAttempt?.resolution ?? 'pending',
      });

  useEffect(() => {
    if (preLeaseStatus !== 'ok' || !ownerId || transitionBarrier.active) return;
    const controller = new AbortController();
    void holdSharedOwnerRuntimeLock(controller.signal, async () => {
      const currentBinding = await getContentOwnerBinding();
      if (controller.signal.aborted) return false;
      if (currentBinding !== ownerId) {
        setBinding((current) => retainLoadedBinding(current, currentBinding));
        setMatchingOwnerReady(null);
        return false;
      }
      setRuntimeLease({
        ownerId,
        attempt,
        barrierEpoch: transitionBarrier.epoch,
        state: 'ready',
      });
      return true;
    // The owner's voice captures keep this lease after its abort until they
    // settle, or until the verified failure hand-off (plan rev 4.1, 6.2).
    }, ownerId).catch(() => {
      if (!controller.signal.aborted) {
        setRuntimeLease({
          ownerId,
          attempt,
          barrierEpoch: transitionBarrier.epoch,
          state: 'error',
        });
      }
    });
    return () => controller.abort();
  }, [attempt, ownerId, preLeaseStatus, transitionBarrier.active, transitionBarrier.epoch]);

  const activeRuntimeLease = runtimeLease?.ownerId === ownerId &&
    runtimeLease.attempt === attempt &&
    runtimeLease.barrierEpoch === transitionBarrier.epoch
      ? runtimeLease
      : null;
  const status = transitionBarrier.active
    ? 'resolving'
    : preLeaseStatus === 'ok'
      ? activeRuntimeLease?.state === 'error'
        ? 'connection-error'
        : activeRuntimeLease?.state === 'ready'
          ? 'ok'
          : 'resolving'
      : preLeaseStatus;

  const retry = useCallback(() => {
    setAttemptResult(null);
    setMatchingOwnerReady(null);
    setAttempt((value) => value + 1);
  }, []);

  // A failed network bootstrap resumes quietly; storage failures remain manual.
  useEffect(() => {
    if (status !== 'connection-error' || activeAttempt?.resolution !== 'connection-error' ||
        binding?.value === ownerId || matchingFailed || activeRuntimeLease?.state === 'error') return;
    const resume = () => {
      if (navigator.onLine && document.visibilityState === 'visible') retry();
    };
    window.addEventListener('online', resume);
    document.addEventListener('visibilitychange', resume);
    return () => {
      window.removeEventListener('online', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [status, retry, activeAttempt?.resolution, binding?.value, ownerId, matchingFailed, activeRuntimeLease?.state]);

  // The storage screen's Check again. It repeats the required reads from the
  // start (binding, then the unbound inspection) for the session owner current
  // when it was asked, and discards its result if the owner changed at all
  // meanwhile (even A to B to A), or the owner-transition barrier did: the
  // session and barrier effects above then re-read for the new state. It never binds, adopts or
  // opens anything itself. A readable result is handed back to the normal gate,
  // which repeats its checks and takes the transition lock before any install.
  const checkStorageAgain = useCallback((): Promise<OwnerStorageCheckResult> => {
    const startOwner = currentSessionOwnerRef.current;
    const startSessionChanges = sessionOwnerChangesRef.current;
    const startBarrierNotices = barrierNoticesRef.current;
    const isCurrent = () =>
      mountedRef.current &&
      currentSessionOwnerRef.current === startOwner &&
      sessionOwnerChangesRef.current === startSessionChanges &&
      barrierNoticesRef.current === startBarrierNotices &&
      !isOwnerTransitionPresentationBarrierActive();
    if (!isCurrent()) return Promise.resolve('discarded');
    const running = startOwnerStorageRecheck(storageCheckGuardRef.current, {
      readBinding: getContentOwnerBinding,
      inspectUnbound: inspectUnboundContentOwnerProfileDetailed,
      isCurrent,
      isClientUpdateRequiredError,
    });
    if (running === null) return Promise.resolve('already-running');
    return running.then(
      (outcome): OwnerStorageCheckResult => {
        if (outcome.kind === 'stale' || !mountedRef.current) return 'discarded';
        if (outcome.kind === 'client-update-required') {
          setClientUpdateRequired(true);
          return 'continued';
        }
        if (outcome.kind === 'unreadable') {
          if (outcome.stage === 'binding') {
            setBinding((current) => retainUnreadableBinding(current));
          }
          return 'still-unreadable';
        }
        setBinding((current) => retainLoadedBinding(current, outcome.binding));
        setAttemptResult(null);
        setMatchingOwnerReady(null);
        setAttempt((value) => value + 1);
        return 'continued';
      },
      // The recheck catches its own read failures; anything else keeps the
      // screen closed.
      (): OwnerStorageCheckResult => 'still-unreadable',
    );
  }, []);

  return {
    status,
    knownOwnerId: binding?.value ?? null,
    currentOwnerId: ownerId,
    retry,
    checkStorageAgain,
  };
}
